import { useState } from "react";
import { Mail, Send, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import LegalLayout from "@/components/LegalLayout";
import { api } from "@/lib/book";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const h = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send. Try again or email us directly.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <LegalLayout
      eyebrow="Get in touch"
      title="Contact & Support"
      lastUpdated={null}
      testid="page-contact"
    >
      <p className="text-lg">
        Question about your order, the book, press requests, or anything else — drop a message below or email <a href="mailto:orders@12lawsbook.com" className="underline text-[#D92D20]">orders@12lawsbook.com</a>. We reply within 2 business days.
      </p>

      {done ? (
        <div className="mt-8 bg-white border border-black/10 p-7" data-testid="contact-success">
          <div className="w-12 h-12 bg-[#D92D20] flex items-center justify-center mb-5">
            <Check className="w-6 h-6 text-white" />
          </div>
          <h2 className="font-display font-bold text-2xl tracking-tight">Message received.</h2>
          <p className="mt-3 text-[#525252]">
            Thanks {form.name.split(" ")[0]}. We'll get back to you at <span className="text-[#0A0A0A] font-medium">{form.email}</span>.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 bg-white border border-black/10 p-7 space-y-4" data-testid="contact-form">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Your name" v={form.name} onChange={h("name")} testid="contact-name" />
            <Field label="Email" type="email" v={form.email} onChange={h("email")} testid="contact-email" />
          </div>
          <Field label="Subject (optional)" v={form.subject} onChange={h("subject")} testid="contact-subject" />
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#525252] font-bold">Message</label>
            <textarea
              rows={6}
              value={form.message}
              onChange={h("message")}
              className="mt-2 w-full p-3 bg-white border border-black/15 focus:border-[#0A0A0A] focus:outline-none text-sm leading-relaxed"
              data-testid="contact-message"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="inline-flex items-center gap-3 bg-[#D92D20] text-white px-7 py-4 font-medium hover:bg-[#B42318] transition-colors disabled:opacity-60"
            data-testid="contact-submit"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            Send message
          </button>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#737373]">
            <Mail className="w-3 h-3 inline mr-1" /> We never share your email
          </p>
        </form>
      )}
    </LegalLayout>
  );
}

function Field({ label, v, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-[#525252] font-bold">{label}</label>
      <input
        type={type}
        value={v}
        onChange={onChange}
        className="mt-2 w-full h-12 px-3 bg-white border border-black/15 focus:border-[#0A0A0A] focus:outline-none text-sm"
        data-testid={testid}
      />
    </div>
  );
}
